import { useEffect, useMemo, useState } from 'react';
import { createResource,getResources,createResourceWithFile, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import InvoiceForm from './InvoiceForm';


/* ================= STATUTS ================= */
type BonLivraisonStatut = 'BROUILLON' | 'VALIDE' | 'PARTIEL' | 'REJETE' | 'FACTURE';

const STATUS_LABELS: Record<BonLivraisonStatut, string> = {
  BROUILLON: 'Brouillon',
  VALIDE: 'Livré',
  PARTIEL: 'Livraison partielle',
  REJETE: 'Rejeté',
  FACTURE: 'facturer'
};

const STATUS_COLORS: Record<BonLivraisonStatut, string> = {
  BROUILLON: 'bg-gray-100 text-gray-800',
  VALIDE: 'bg-green-100 text-green-800',
  PARTIEL: 'bg-orange-100 text-orange-800',
  REJETE: 'bg-red-100 text-red-800',
  FACTURE: 'bg-blue-100 text-blue-800'
};

/* ================= TYPES ================= */
interface DeliveryNote {
  id: number;
  numero: string;
  bon_commande_numero: string;
  date_livraison: string;
  statut: BonLivraisonStatut;
  est_facture: boolean;
  total_ttc: number;
  client: {
    id: number;
    raison_sociale: string;
    email?: string;
  };
  preuve_reception_type: 'MANUSCRITE' | 'ELECTRONIQUE';
  preuve_reception_fichier: string | null;
  date_signature: string | null;
  signe_par: string | null;
  signe: boolean;
}

/* ================= Preuve Reception Modal ================= */
function PreuveReceptionModal({
  bl,
  onSuccess,
}: {
  bl: DeliveryNote;
  onSuccess: () => void;
}) {
  const [open, setOpen] = useState(false);

  const [type, setType] = useState<'MANUSCRITE' | 'ELECTRONIQUE'>(
  bl.preuve_reception_type === 'ELECTRONIQUE'
    ? 'ELECTRONIQUE'
    : 'MANUSCRITE'
);

  const [fichier, setFichier] = useState<File | null>(null);
  const [signePar, setSignePar] = useState(bl.signe_par || '');
  const [loading, setLoading] = useState(false);

  const isSigned = bl.signe;

  const handleSubmit = async () => {
  if (!fichier) {
    toast.error('Veuillez ajouter le fichier de preuve');
    return;
  }

  if (!signePar.trim()) {
    toast.error('Veuillez indiquer le nom du signataire');
    return;
  }

  const formData = new FormData();
  formData.append('preuve_reception_type', type);
  formData.append('signe_par', signePar);

  if (fichier) {
    formData.append('preuve_reception_fichier', fichier);
  }

  setLoading(true);

  try {
    await createResourceWithFile(
      `bons-livraison/${bl.id}/preuve-reception`,
      formData
    );

    toast.success('Preuve enregistrée avec succès');
    setOpen(false);
    onSuccess();
  } catch (err: any) {
    toast.error(err.response?.data?.message || 'Erreur lors de la sauvegarde');
  } finally {
    setLoading(false);
  }
};


  const getTypeLabel = (t: string) => {
    switch (t) {
      case 'MANUSCRITE': return 'Signature manuscrite';
      case 'ELECTRONIQUE': return 'Signature électronique';
      default: return 'Aucune';
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`px-3 py-1.5 text-xs rounded-full font-medium transition ${
          isSigned
            ? 'bg-green-100 text-green-800 hover:bg-green-200'
            : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
        }`}
      >
        {isSigned ? 'Preuve enregistrée' : 'Ajouter preuve'}
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b dark:border-gray-700">
              <h2 className="text-xl font-bold">
                Preuve de réception - BL {bl.numero}
              </h2>
              <button
                onClick={() => setOpen(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {isSigned ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-green-700 dark:text-green-400">
                    <i className="ri-check-double-line text-2xl" />
                    <span className="font-medium">Preuve déjà enregistrée</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Type</p>
                      <p className="font-medium">{getTypeLabel(bl.preuve_reception_type)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Signé par</p>
                      <p className="font-medium">{bl.signe_par || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Date</p>
                      <p className="font-medium">
                        {bl.date_signature
                          ? new Date(bl.date_signature).toLocaleString('fr-FR')
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Preuve</p>
                      {bl.preuve_reception_fichier ? (
                        <a
                          href={`/storage/${bl.preuve_reception_fichier}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          Voir le fichier
                        </a>
                      ) : (
                        '-'
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Type de preuve
                    </label>
                    <select
                      value={type}
                      onChange={(e) => setType(e.target.value as any)}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="MANUSCRITE">Manuscrite (photo)</option>
                      <option value="ELECTRONIQUE">Électronique</option>
                    </select>
                  </div>

                  <>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Fichier (photo/PDF)
                      </label>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setFichier(e.target.files?.[0] || null)}
                        className="w-full px-4 py-2 border rounded-lg"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Signé par
                      </label>
                      <input
                        type="text"
                        value={signePar}
                        onChange={(e) => setSignePar(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      />
                    </div>
                  </>

                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t dark:border-gray-700">
              <button
                onClick={() => setOpen(false)}
                className="px-5 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {isSigned ? 'Fermer' : 'Annuler'}
              </button>

              {!isSigned && (
                <button
                  onClick={handleSubmit}
                  disabled={loading }
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {loading && <i className="ri-loader-4-line animate-spin" />}
                  Enregistrer
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ================= COMPONENT PRINCIPAL ================= */
export default function DeliveryNotesList() {
  const [notes, setNotes] = useState<DeliveryNote[]>([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] =
    useState<'all' | BonLivraisonStatut>('all');

  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [selectedBL, setSelectedBL] = useState<DeliveryNote | null>(null);

  const navigate = useNavigate();

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const res = await getResources('bons-livraison', {
        params: {
          search: search || undefined,
          statut: statusFilter !== 'all' ? statusFilter : undefined,
        },
      });
      setNotes(res.data.data ?? []);
      console.log('SIGNE:', res);

    } catch {
      toast.error('Erreur lors du chargement des bons de livraison');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [search, statusFilter]);

  const filteredNotes = useMemo(() => notes, [notes]);

  const openInvoiceForm = (bl: DeliveryNote) => {
    if (bl.est_facture) {
      toast.error('Ce bon de livraison est déjà facturé');
      return;
    }
    setSelectedBL(bl);
    setShowInvoiceForm(true);
  };

    /* ================= IMPRESSION BON DE LIVRAISON - VERSION VERTE + 2 SIGNATURES ================= */
  const handlePrint = (bl: DeliveryNote) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Impossible d'ouvrir la fenêtre d'impression");
      return;
    }

    const totalTTC = Number(bl.total_ttc);
    const totalHT = bl.total_ht ?? totalTTC / 1.1925;
    const tvaMontant = bl.total_tva ?? totalTTC - totalHT;

    // À adapter : assure-toi que ton API renvoie les lignes du BL
    const lignes = bl.lignes || [];

    const html = `<!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="utf-8">
      <title>Bon de Livraison ${bl.numero}</title>
      <style>
        body { 
          font-family: 'Segoe UI', Arial, sans-serif; 
          margin: 40px; 
          background: #f9fafb; 
          color: #333; 
        }
        .container { 
          max-width: 900px; 
          margin: 0 auto; 
          background: white; 
          padding: 40px; 
          border-radius: 8px; 
          box-shadow: 0 4px 20px rgba(0,0,0,0.1); 
        }
        .header { 
          text-align: center; 
          padding-bottom: 20px; 
          border-bottom: 3px solid #16a34a; /* Vert principal */
        }
        .header h1 { 
          color: #15803d; 
          margin: 0; 
          font-size: 32px; 
        }
        .header h2 { 
          color: #166534; 
          margin: 10px 0 0; 
          font-size: 24px; 
        }

        .info-grid { 
          display: grid; 
          grid-template-columns: 1fr 1fr; 
          gap: 30px; 
          margin: 30px 0; 
        }
        .info-block h3 { 
          color: #15803d; 
          border-bottom: 1px solid #bbf7d0; 
          padding-bottom: 8px; 
          margin-bottom: 12px; 
        }

        table { 
          width: 100%; 
          border-collapse: collapse; 
          margin: 30px 0; 
        }
        th, td { 
          border: 1px solid #e5e7eb; 
          padding: 12px; 
        }
        th { 
          background: #f0fdf4; /* Vert très clair */
          color: #15803d; 
        }

        .text-right { text-align: right; }
        .text-center { text-align: center; }

        .totals { 
          float: right; 
          width: 400px; 
          margin-top: 30px; 
        }
        .total-line { 
          display: flex; 
          justify-content: space-between; 
          padding: 8px 0; 
          border-top: 1px solid #e5e7eb; 
        }
        .total-final { 
          font-size: 18px; 
          font-weight: bold; 
          color: #15803d; 
          border-top: 2px solid #16a34a; 
          padding-top: 12px; 
        }

        .notes { 
          margin-top: 50px; 
          padding: 20px; 
          background: #f0fdf4; 
          border-radius: 8px; 
          border-left: 4px solid #16a34a;
        }

        /* ZONE DE SIGNATURE - DEUX PASSAGES OBLIGATOIRES */
        .signature-section {
          margin-top: 100px;
          page-break-inside: avoid;
          clear: both;
        }
        .signature-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          margin-top: 40px;
        }
        .signature-block {
          text-align: center;
        }
        .signature-label {
          font-weight: bold;
          font-size: 16px;
          margin-bottom: 20px;
          color: #15803d;
        }
        .signature-line {
          border-top: 2px dotted #666;
          height: 120px;
          margin: 20px 0;
          position: relative;
        }
        .signature-line::after {
          content: "Signature + Cachet";
          position: absolute;
          bottom: -25px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 13px;
          color: #666;
          background: white;
          padding: 0 15px;
          font-weight: bold;
        }
        .signature-info {
          margin-top: 40px;
          font-size: 14px;
          line-height: 1.8;
        }

        .proof-image {
          max-width: 350px;
          margin: 20px auto;
          border: 2px solid #16a34a;
          border-radius: 10px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .footer { 
          margin-top: 100px; 
          text-align: center; 
          color: #6b7280; 
          font-size: 12px; 
        }

        @media print {
          body { background: white; margin: 0; }
          .container { box-shadow: none; padding: 20px; }
        }
      </style>
    </head>
    <body>
      <div class="container">

        <div class="header">
          <h1>BON DE LIVRAISON</h1>
          <h2>N° ${bl.numero}</h2>
        </div>

        <div class="info-grid">
          <div class="info-block">
            <h3>Client destinataire</h3>
            <div><strong>Raison sociale :</strong> ${bl.client.raison_sociale}</div>
            <div><strong>Email :</strong> ${bl.client.email || 'Non renseigné'}</div>
          </div>

          <div class="info-block">
            <h3>Informations livraison</h3>
            <div><strong>Date de livraison :</strong> ${new Date(bl.date_livraison).toLocaleDateString('fr-FR')}</div>
            <div><strong>Référence commande :</strong> ${bl.bon_commande_numero}</div>
            <div><strong>Statut :</strong> ${STATUS_LABELS[bl.statut]}</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Référence</th>
              <th>Désignation</th>
              <th class="text-center">Qté commandée</th>
              <th class="text-center">Qté livrée</th>
              <th class="text-right">Prix U. HT</th>
              <th class="text-right">Total HT</th>
            </tr>
          </thead>
          <tbody>
            ${lignes.length > 0 ? lignes.map((l: any) => `
              <tr>
                <td>${l.produit_reference || '—'}</td>
                <td>${l.designation}</td>
                <td class="text-center">${l.quantite_commandee || l.quantite}</td>
                <td class="text-center">${l.quantite_livree || l.quantite}</td>
                <td class="text-right">${Number(l.prix_unitaire_ht || l.prix_unitaire).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</td>
                <td class="text-right">${Number(l.total_ht || l.total).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</td>
              </tr>
            `).join('') : `
              <tr>
                <td colspan="6" class="text-center text-gray-500 py-8">
                  Lignes non disponibles (à charger via l'API)
                </td>
              </tr>
            `}
          </tbody>
        </table>

        <div class="totals">
          <div class="total-line">
            <span>Total HT :</span>
            <span>${totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</span>
          </div>
          <div class="total-line">
            <span>TVA (19.25%) :</span>
            <span>${tvaMontant.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</span>
          </div>
          <div class="total-line total-final">
            <span>Total TTC :</span>
            <span>${totalTTC.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</span>
          </div>
        </div>

        <div style="clear:both;"></div>

        <!-- ZONE DE SIGNATURE - DEUX PASSAGES OBLIGATOIRES -->
        <div class="signature-section">
          <h3 style="text-align: center; color: #15803d; margin-bottom: 30px; font-size: 20px;">
            ACCUSÉ DE RÉCEPTION DES MARCHANDISES
          </h3>

          ${bl.signe ? `
            <div style="text-align: center; color: #16a34a; font-weight: bold; margin-bottom: 30px; font-size: 18px;">
              ✓ Preuve de réception enregistrée numériquement
            </div>
            <div style="text-align: center; margin-bottom: 40px;">
              <p><strong>Type :</strong> ${bl.preuve_reception_type === 'MANUSCRITE' ? 'Signature manuscrite (photo)' : 'Signature électronique'}</p>
              <p><strong>Signé par :</strong> ${bl.signe_par || '—'}</p>
              <p><strong>Le :</strong> ${bl.date_signature ? new Date(bl.date_signature).toLocaleString('fr-FR') : '—'}</p>
              ${bl.preuve_reception_fichier ? `
                <img src="http://groupe-2ms.com /storage/${bl.preuve_reception_fichier}" class="proof-image" alt="Preuve de réception">
              ` : ''}
            </div>
          ` : `
            <div style="text-align: center; color: #d97706; font-weight: bold; margin-bottom: 30px;">
              En attente de preuve de réception
            </div>
          `}

          <div class="signature-grid">
            <!-- 1ER PASSAGE : LIVREUR / MAGASINIER -->
            <div class="signature-block">
              <div class="signature-label">
                1. Livreur / Magasinier<br>
                <small>(Cachet + Signature obligatoire)</small>
              </div>
              <div class="signature-line"></div>
              <div class="signature-info">
                <div>Date : _____________________</div>
                <div>Nom : _____________________</div>
                <div>Fonction : __________________</div>
              </div>
            </div>

            <!-- 2ÈME PASSAGE : CLIENT / RÉCEPTIONNAIRE -->
            <div class="signature-block">
              <div class="signature-label">
                2. Client / Réceptionnaire<br>
                <small>(Cachet + Signature obligatoire)</small>
              </div>
              <div class="signature-line"></div>
              <div class="signature-info">
                <div>Date : _____________________</div>
                <div>Nom : _____________________</div>
                <div>Fonction : __________________</div>
              </div>
            </div>
          </div>

          <p style="text-align: center; margin-top: 50px; font-style: italic; color: #666; font-size: 15px;">
            "J'accuse réception des marchandises mentionnées ci-dessus en bon état, conformes à la commande et en quantités exactes."
          </p>
        </div>

        <div class="footer">
          Document généré le ${new Date().toLocaleDateString('fr-FR')}
          à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </div>

      </div>
    </body>
    </html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 1200);
  };

  const getPrintTitle = (bl: DeliveryNote) => {
    return bl.est_facture ? 'Imprimer la facture' : 'Imprimer le BL';
  };

  const getPrintIcon = (bl: DeliveryNote) => {
    return bl.est_facture ? 'ri-file-text-line' : 'ri-printer-line';
  };

  return (
    <div className="space-y-6">

      {/* FILTRES */}
      <div className="flex gap-4">
        <input
          className="px-3 py-2 border rounded-md flex-1"
          placeholder="Recherche BL / Client…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="px-3 py-2 border rounded-md"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
        >
          <option value="all">Tous les statuts</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* TABLE */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y">
          <thead className="bg-gray-50">
            <tr>
              <Th>BL</Th>
              <Th>BCC</Th>
              <Th>Client</Th>
              <Th>Date</Th>
              <Th>Montant TTC</Th>
              <Th>Statut</Th>
              <Th className="text-center">Actions</Th>
            </tr>
          </thead>

          <tbody className="divide-y">
            {loading && (
              <tr>
                <td colSpan={7} className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                </td>
              </tr>
            )}

            {!loading && filteredNotes.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-12 text-gray-500">
                  Aucun bon de livraison trouvé
                </td>
              </tr>
            )}

            {!loading && filteredNotes.map(bl => (
              <tr key={bl.id} className="hover:bg-gray-50">

                <Td className="font-medium text-indigo-600">{bl.numero}</Td>
                <Td>{bl.bon_commande_numero}</Td>

                <Td>
                  <div className="font-medium">{bl.client.raison_sociale}</div>
                  {bl.client.email && (
                    <div className="text-xs text-gray-500">{bl.client.email}</div>
                  )}
                </Td>

                <Td>{new Date(bl.date_livraison).toLocaleDateString('fr-FR')}</Td>

                <Td className="font-medium">
                  {Number(bl.total_ttc).toLocaleString('fr-FR')} GNF
                </Td>

                <Td>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${STATUS_COLORS[bl.statut]}`}>
                      {STATUS_LABELS[bl.statut]}
                    </span>
                  </div>
                </Td>
                <Td>
                  <div className="flex gap-3">

                    {/* TOUJOURS AFFICHÉ : Détail */}
                    <IconBtn
                        title="Détail"
                        icon="ri-eye-line"
                        className="text-indigo-600"
                      onClick={() => navigate(`/sales-detail/${bl.id}`)}
                    />

                    {/* ÉTAPE 1 : Pas de preuve → Imprimer + Ajouter preuve */}
                    {!bl.signe && (
                      <>
                        {/* Imprimer */}
                        <IconBtn
                          icon="ri-printer-line"
                          title="Imprimer le bon de livraison"
                          className="text-gray-600 hover:text-gray-800"
                          onClick={() => handlePrint(bl)}
                        />

                        {/* Ajouter preuve */}
                        <PreuveReceptionModal bl={bl} onSuccess={fetchNotes} />
                      </>
                    )}

                    {/* ÉTAPE 2 : Preuve enregistrée → Créer facture (si conditions OK) */}
                    {bl.signe == true && 
                    !bl.est_facture && 
                    (bl.statut === 'VALIDE' || bl.statut === 'PARTIEL') && (
                      <IconBtn
                        icon="ri-file-add-line"
                        title="Créer la facture"
                        className="text-blue-600 hover:text-blue-800"
                        onClick={() => openInvoiceForm(bl)}
                      />
                    )}

                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MODAL FACTURE */}
      {showInvoiceForm && selectedBL && (
        <InvoiceForm
          customerId={selectedBL.client.id}
          blId={selectedBL.id}
          onClose={() => {
            setShowInvoiceForm(false);
            setSelectedBL(null);
          }}
          onSuccess={() => {
            toast.success('Facture créée avec succès !');
            setShowInvoiceForm(false);
            setSelectedBL(null);
            fetchNotes();
          }}
        />
      )}
    </div>
  );
}

/* ================= UI HELPERS ================= */
function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-6 py-4 text-sm text-gray-900 ${className}`}>{children}</td>;
}

function IconBtn({
  icon,
  title,
  onClick,
  className = '',
}: {
  icon: string;
  title: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={title}
      className={`p-2 rounded-lg hover:bg-gray-100 transition ${className}`}
    >
      <i className={`${icon} text-xl`} />
    </button>
  );
}